import { http } from './request'
import {
  USER_ORG_ID_KEY,
  USER_REGION_CODE_KEY,
  getStoredSimpleLoginUserJson,
  persistSimpleLoginUserJson,
  persistUserDisplayInfo,
} from './auth-storage'
import { secureLocalStorage } from '@/utils/secureLocalStorage'

export {
  USER_REGION_CODE_KEY,
  USER_ORG_ID_KEY,
  SIMPLE_LOGIN_USER_JSON_KEY,
  clearUserRegionOrgLocal,
  USER_REAL_NAME_KEY,
  USER_DEPARTMENT_KEY,
  USER_ROLE_KEY,
  getStoredUserRealName,
  getStoredUserDepartment,
  getStoredUserRole,
  getStoredSimpleLoginUserJson,
  persistSimpleLoginUserJson,
} from './auth-storage'

/** 任意成功拉取 getSimpleLoginUser 后写入完整 JSON，与各封装函数共用 */
function cacheGetSimpleLoginUserPayload(data: unknown): void {
  persistSimpleLoginUserJson(data)
}

/** 当前登录用户资料（前端展示用，由 GET /auth/b/getSimpleLoginUser 映射而来） */
export interface AuthProfile {
  id: string
  username: string
  realName: string
  phone: string
  /** 地区（如省市区或驻外地区） */
  region: string
  department: string
  role: string
  avatarUrl?: string | null
  /** 最近登录时间，如 2025-03-24 09:30 */
  lastLoginAt?: string
  /** 密码最后修改时间 */
  passwordUpdateTime?: string
  /** 账号创建时间（改密时间为空时作为有效期起点） */
  createTime?: string
  /** 密码过期时间（上次改密 + 90 天） */
  passwordExpireTime?: string
  /** 密码是否已过期 */
  passwordExpired?: boolean
  /** 距过期剩余天数，已过期为 0 */
  passwordRemainDays?: number
}

/** 与 token 一并写入本地，供业务使用（退出登录时清除） */

export function getStoredUserRegionCode(): string | null {
  return secureLocalStorage.getItem(USER_REGION_CODE_KEY)
}

export function getStoredUserOrgId(): string | null {
  return secureLocalStorage.getItem(USER_ORG_ID_KEY)
}

function persistUserRegionOrg(regionCode: unknown, orgId: unknown): void {
  const rc = regionCode != null && String(regionCode).trim() !== '' ? String(regionCode) : ''
  const oid = orgId != null && String(orgId).trim() !== '' ? String(orgId) : ''
  if (rc) secureLocalStorage.setItem(USER_REGION_CODE_KEY, rc)
  else secureLocalStorage.removeItem(USER_REGION_CODE_KEY)
  if (oid) secureLocalStorage.setItem(USER_ORG_ID_KEY, oid)
  else secureLocalStorage.removeItem(USER_ORG_ID_KEY)
}

/** 手机号脱敏：11 位大陆手机保留前 3 后 4，中间以 **** 代替 */
export function maskPhone(phone: string): string {
  const p = phone.trim()
  if (p.length === 11 && /^1\d{10}$/.test(p)) {
    return `${p.slice(0, 3)}****${p.slice(-4)}`
  }
  if (p.length <= 4) return p ? '****' : '—'
  return `${p.slice(0, 2)}****${p.slice(-2)}`
}

/** 修改密码请求体：原密码 + 短信验证码 + 新密码 */
export interface ChangePasswordPayload {
  oldPassword: string
  smsCode: string
  newPassword: string
}

/** GET /auth/b/getSimpleLoginUser 解包后的 data */
export interface LoginUserInfo {
  /** 用户 id */
  id?: string
  /** 姓名 */
  name: string
  /** 用户名 */
  account: string
  phone: string
  /** 地区 */
  region: string
  /** 部门名称 */
  orgName: string
  /** 角色（字符串或字符串数组） */
  roleNames: string | string[]
  /** 最近登录时间（后端可能返回以下任一字段名，映射层会统一为 AuthProfile.lastLoginAt） */
  latestLoginTime?: string
  lastLoginTime?: string
  lastLoginAt?: string
  /** 密码最后修改时间 */
  passwordUpdateTime?: string
  /** 账号创建时间 */
  createTime?: string
  /** 密码过期时间 */
  passwordExpireTime?: string
  passwordExpired?: boolean
  passwordRemainDays?: number
  /** 头像（URL 等） */
  avatar: string
  /** 地区编码（写入本地） */
  regionCode: string
  /** 部门编号（写入本地） */
  orgId: string | number
}

function normalizeRoleNames(roleNames: string | string[] | undefined): string {
  if (roleNames == null) return ''
  if (Array.isArray(roleNames)) return roleNames.map(String).join(', ')
  return String(roleNames)
}

/** 新接口字段名可能与旧 getLoginUser 的 latestLoginTime 不一致，按常见命名依次兜底 */
function resolveLastLoginAt(data: Partial<LoginUserInfo>): string | undefined {
  const raw = data as Record<string, unknown>
  const candidates = [
    data.latestLoginTime,
    data.lastLoginTime,
    data.lastLoginAt,
    raw.latestLoginAt,
    raw.latestLoginDate,
    raw.lastLoginDate,
  ]
  for (const v of candidates) {
    if (v != null && String(v).trim() !== '') return String(v)
  }
  return undefined
}

function mapLoginUserToAuthProfile(data: Partial<LoginUserInfo>): AuthProfile {
  const avatar = data.avatar?.trim()
  const account = data.account ?? ''
  return {
    id: data.id != null && String(data.id).trim() !== '' ? String(data.id) : account,
    username: account,
    realName: data.name ?? '',
    phone: data.phone ?? '',
    region: data.region ?? '',
    department: data.orgName ?? '',
    role: normalizeRoleNames(data.roleNames),
    lastLoginAt: resolveLastLoginAt(data),
    passwordUpdateTime:
      data.passwordUpdateTime != null && String(data.passwordUpdateTime).trim() !== ''
        ? String(data.passwordUpdateTime)
        : undefined,
    createTime:
      data.createTime != null && String(data.createTime).trim() !== ''
        ? String(data.createTime)
        : undefined,
    passwordExpireTime:
      data.passwordExpireTime != null && String(data.passwordExpireTime).trim() !== ''
        ? String(data.passwordExpireTime)
        : undefined,
    passwordExpired: Boolean(data.passwordExpired),
    passwordRemainDays:
      typeof data.passwordRemainDays === 'number' ? data.passwordRemainDays : undefined,
    avatarUrl: avatar ? avatar : null,
  }
}

/** 合并同一时刻对 GET /auth/b/getSimpleLoginUser 的并发请求（仍会与顺序触发的第二次调用各发一次 HTTP） */
let inflightSimpleLoginUser: Promise<Partial<LoginUserInfo>> | null = null

/**
 * 原始登录用户 payload（与 permission / 资料接口同源）
 */
export async function fetchSimpleLoginUserRaw(): Promise<Partial<LoginUserInfo>> {
  if (!inflightSimpleLoginUser) {
    inflightSimpleLoginUser = http
      .get<Partial<LoginUserInfo>>('/auth/b/getSimpleLoginUser')
      .finally(() => {
        inflightSimpleLoginUser = null
      })
  }
  return inflightSimpleLoginUser
}

/**
 * 将 getSimpleLoginUser 的 data 写入缓存、本地展示字段并映射为 AuthProfile（供权限/bootstrap 与资料刷新共用）
 */
export function applySimpleLoginUserPayload(data: Partial<LoginUserInfo>): AuthProfile {
  cacheGetSimpleLoginUserPayload(data)
  persistUserRegionOrg(data.regionCode, data.orgId)
  const profile = mapLoginUserToAuthProfile(data)
  persistUserDisplayInfo({
    realName: profile.realName,
    department: profile.department,
    role: profile.role,
  })
  return profile
}

/**
 * 获取当前用户信息（需登录态，GET /auth/b/getSimpleLoginUser）
 * 成功后将 regionCode、orgId、姓名、部门、角色写入 localStorage
 */
export async function fetchAuthProfile(): Promise<AuthProfile> {
  const data = await fetchSimpleLoginUserRaw()
  return applySimpleLoginUserPayload(data)
}

/**
 * 修改密码（需原密码与手机验证码校验）
 * 成功后前端应清除登录态并跳转登录页。
 */
export async function changePassword(payload: ChangePasswordPayload): Promise<void> {
  await http.post<void>('/auth/password/change', payload)
}

// —— 登录页：图形验证码与账号登录（与后端 /auth/b/* 对齐）——

/** GET /auth/b/getPicCaptcha，解包后的 data（图片与流水号字段名由后端决定，页面侧再解析） */
export async function fetchPicCaptcha(): Promise<unknown> {
  return http.get<unknown>('/auth/b/getPicCaptcha')
}

/** GET /auth/b/loginFactors */
export interface LoginFactors {
  captchaOpen: boolean
  smsOpen: boolean
}

function asFlag(v: unknown, fallback: boolean): boolean {
  if (v === true || v === 1 || v === '1' || v === 'true' || v === 'TRUE') return true
  if (v === false || v === 0 || v === '0' || v === 'false' || v === 'FALSE') return false
  return fallback
}

export async function fetchLoginFactors(): Promise<LoginFactors> {
  const raw = await http.get<Partial<LoginFactors> & Record<string, unknown>>(
    '/auth/b/loginFactors',
  )
  return {
    captchaOpen: asFlag(raw?.captchaOpen ?? raw?.captcha_open, true),
    smsOpen: asFlag(raw?.smsOpen ?? raw?.sms_open, true),
  }
}

/** POST /auth/b/doLogin；password 为 SM2 加密后的密文 */
export interface LoginByPasswordPayload {
  account: string
  password: string
  validCode: string
  validCodeReqNo: string
  userMode: number
  /** 短信验证码（登录短信开关开启时必填） */
  phoneValidCode?: string
  /** 短信验证码请求号 */
  phoneValidCodeReqNo?: string
}

export type LoginByPasswordResult = string | { token?: string; accessToken?: string }

export async function loginByPassword(
  payload: LoginByPasswordPayload,
): Promise<LoginByPasswordResult> {
  return http.post<LoginByPasswordResult>('/auth/b/doLogin', payload)
}

/** GET /auth/b/getPhoneValidCode 解包后的 data：含手机验证码请求号 */
export interface GetPhoneValidCodeResult {
  validCodeReqNo: string
}

/**
 * 登录页：获取手机验证码
 * Query：phone、validCode（图形验证码）、validCodeReqNo（与密码登录相同的图片验证码流水号）
 */
export async function sendLoginSms(
  phone: string,
  validCode: string,
  validCodeReqNo: string,
  userMode: number,
): Promise<GetPhoneValidCodeResult> {
  return http.get<GetPhoneValidCodeResult>('/auth/b/getPhoneValidCode', {
    params: { phone, validCode, validCodeReqNo, userMode },
  })
}

export interface BoundPhoneValidCodeResult {
  validCodeReqNo: string
  phoneMasked: string
}

/** 向账号绑定手机下发登录短信（不消耗图形验证码） */
export async function sendBoundPhoneLoginSms(payload: {
  account: string
  password: string
  validCode: string
  validCodeReqNo: string
}): Promise<BoundPhoneValidCodeResult> {
  return http.post<BoundPhoneValidCodeResult>('/auth/b/sendBoundPhoneValidCode', payload, {
    timeout: 30_000,
  })
}

/**
 * @deprecated 修改密码请走 `/sys/userCenter/*`（user-validCode），勿再用本方法。
 * 本方法写入 auth-validCode，与 findPasswordByPhone 读取的 user-validCode 不一致。
 */
export async function sendChangePasswordSms(
  phone: string,
  validCode: string,
  validCodeReqNo: string,
): Promise<GetPhoneValidCodeResult> {
  return sendLoginSms(phone, validCode, validCodeReqNo, 100)
}

/** POST /auth/b/doLoginByPhone */
export interface LoginByPhonePayload {
  phone: string
  validCode: string
  validCodeReqNo: string
  userMode: number
}

/** 手机号码登录（短信验证码，与密码登录一致，token 可能为字符串或对象字段） */
export async function loginBySms(payload: LoginByPhonePayload): Promise<LoginByPasswordResult> {
  return http.post<LoginByPasswordResult>('/auth/b/doLoginByPhone', {
    phone: payload.phone,
    validCode: payload.validCode,
    validCodeReqNo: payload.validCodeReqNo,
    userMode: payload.userMode ?? 100,
  })
}

// —— 扫码登录（PC 展示二维码，移动端 App 扫码确认；轮询状态后写入 token）——

/** 扫码会话状态（前端归一化后） */
export type QrLoginStatus = 'pending' | 'scanned' | 'confirmed' | 'expired' | 'cancelled'

/** GET /auth/b/getQrLoginCode 解包后的 data */
export interface QrLoginCodeResult {
  /** 会话 ID，轮询状态时回传 */
  qrLoginId: string
  /** 写入二维码图案的文本（URL / 协议串 / ticket） */
  qrContent: string
  /** 有效期（秒），缺省按 120 */
  expireSeconds: number
  /** 若后端直接返回图片 base64，优先用图展示 */
  qrImgBase64?: string
}

/** GET /auth/b/getQrLoginStatus 解包后的 data */
export interface QrLoginStatusResult {
  status: QrLoginStatus
  /** status=confirmed 时返回，与密码/短信登录同源 */
  token?: string
}

function asTrimmedString(v: unknown): string {
  if (v == null) return ''
  const s = String(v).trim()
  return s
}

function pickFirstString(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const s = asTrimmedString(obj[k])
    if (s) return s
  }
  return ''
}

function pickExpireSeconds(obj: Record<string, unknown>): number {
  for (const k of ['expireSeconds', 'expiresIn', 'expireIn', 'expireTime']) {
    const n = Number(obj[k])
    if (Number.isFinite(n) && n > 0) return Math.floor(n)
  }
  return 120
}

function toCaptchaLikeImgSrc(raw: string): string {
  const s = raw.trim()
  if (!s) return ''
  if (s.startsWith('data:')) return s
  if (s.startsWith('http://') || s.startsWith('https://')) return s
  return `data:image/png;base64,${s}`
}

/** 将后端 status / state / 数字码归一为前端状态 */
export function normalizeQrLoginStatus(raw: unknown): QrLoginStatus {
  if (raw == null) return 'pending'
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const map: Record<number, QrLoginStatus> = {
      0: 'pending',
      1: 'scanned',
      2: 'confirmed',
      3: 'expired',
      4: 'cancelled',
    }
    return map[raw] ?? 'pending'
  }
  const s = String(raw).trim().toLowerCase()
  if (!s) return 'pending'
  if (['pending', 'wait', 'waiting', 'init', 'created', '未扫码'].includes(s)) return 'pending'
  if (['scanned', 'scan', 'scanning', 'scanned_waiting', '已扫码'].includes(s)) return 'scanned'
  if (
    ['confirmed', 'confirm', 'success', 'succeeded', 'ok', 'done', '已确认', '成功'].includes(s)
  ) {
    return 'confirmed'
  }
  if (['expired', 'timeout', 'expire', '已过期'].includes(s)) return 'expired'
  if (['cancelled', 'canceled', 'cancel', 'reject', 'rejected', '已取消'].includes(s)) {
    return 'cancelled'
  }
  return 'pending'
}

/** 从登录接口返回值提取 token（密码 / 短信 / 扫码共用） */
export function extractLoginToken(res: unknown): string {
  if (typeof res === 'string') return res.trim()
  if (!res || typeof res !== 'object') return ''
  const o = res as Record<string, unknown>
  return pickFirstString(o, ['token', 'accessToken', 'access_token'])
}

function parseQrLoginCodePayload(data: unknown): QrLoginCodeResult | null {
  if (data == null) return null
  if (typeof data === 'string') {
    const id = data.trim()
    if (!id) return null
    return { qrLoginId: id, qrContent: id, expireSeconds: 120 }
  }
  if (typeof data !== 'object') return null
  const o = data as Record<string, unknown>
  const qrLoginId = pickFirstString(o, ['qrLoginId', 'loginId', 'ticket', 'qrCodeId', 'id'])
  const qrContent = pickFirstString(o, [
    'qrContent',
    'qrCode',
    'content',
    'qrUrl',
    'url',
    'ticket',
    'qrLoginId',
  ])
  const imgRaw = pickFirstString(o, ['qrImgBase64', 'qrCodeBase64', 'validCodeBase64', 'base64'])
  if (!qrLoginId) return null
  if (!qrContent && !imgRaw) return null
  return {
    qrLoginId,
    qrContent: qrContent || qrLoginId,
    expireSeconds: pickExpireSeconds(o),
    qrImgBase64: imgRaw ? toCaptchaLikeImgSrc(imgRaw) : undefined,
  }
}

function parseQrLoginStatusPayload(data: unknown): QrLoginStatusResult {
  if (data == null) return { status: 'pending' }
  if (typeof data === 'string') {
    const s = data.trim()
    if (!s) return { status: 'pending' }
    const lower = s.toLowerCase()
    const statusWords = new Set([
      'pending',
      'wait',
      'waiting',
      'init',
      'created',
      'scanned',
      'scan',
      'scanning',
      'scanned_waiting',
      'confirmed',
      'confirm',
      'success',
      'succeeded',
      'ok',
      'done',
      'expired',
      'timeout',
      'expire',
      'cancelled',
      'canceled',
      'cancel',
      'reject',
      'rejected',
      '未扫码',
      '已扫码',
      '已确认',
      '成功',
      '已过期',
      '已取消',
    ])
    if (statusWords.has(lower) || statusWords.has(s)) {
      return { status: normalizeQrLoginStatus(s) }
    }
    return { status: 'confirmed', token: s }
  }
  if (typeof data !== 'object') return { status: 'pending' }
  const o = data as Record<string, unknown>
  const status = normalizeQrLoginStatus(o.status ?? o.state ?? o.qrStatus)
  const token = extractLoginToken(o)
  if (token) return { status: 'confirmed', token }
  return { status }
}

/**
 * 获取扫码登录二维码会话
 * GET /auth/b/getQrLoginCode？userMode（默认 100）
 */
export async function fetchQrLoginCode(userMode = 100): Promise<QrLoginCodeResult> {
  const data = await http.get<unknown>('/auth/b/getQrLoginCode', {
    params: { userMode },
  })
  const parsed = parseQrLoginCodePayload(data)
  if (!parsed) {
    throw new Error('二维码数据格式异常')
  }
  return parsed
}

/**
 * 轮询扫码登录状态
 * GET /auth/b/getQrLoginStatus？qrLoginId
 * 轮询场景默认不弹全局错误，由页面处理过期/失败
 */
export async function fetchQrLoginStatus(qrLoginId: string): Promise<QrLoginStatusResult> {
  const data = await http.get<unknown>('/auth/b/getQrLoginStatus', {
    params: { qrLoginId },
    skipErrorMessage: true,
  })
  return parseQrLoginStatusPayload(data)
}

/** 退出登录（GET /auth/b/doLogout，请求头由拦截器附带 token） */
export async function doLogout(): Promise<void> {
  await http.get<void>('/auth/b/doLogout')
}

/** GET /auth/b/getLoginUser：含 buttonCodeList、roleCodeList（与 yj-admin-ui 登录态一致） */
export interface LoginUserWithPermissions {
  buttonCodeList: string[]
  roleCodeList: string[]
  /** 登录账号，与 getSimpleLoginUser 的 account 一致，用于超管判断 */
  account: string
}

function asStringList(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return v.map((x) => (x != null ? String(x).trim() : '')).filter((s): s is string => Boolean(s))
}

/** 从同源 raw 解析按钮/角色权限列表（与 fetchLoginUserWithPermissions 一致） */
export function loginUserPermissionsFromRaw(raw: Partial<LoginUserInfo>): LoginUserWithPermissions {
  const rec = raw as Record<string, unknown>
  return {
    buttonCodeList: asStringList(rec.buttonCodeList),
    roleCodeList: asStringList(rec.roleCodeList),
    account: raw.account != null ? String(raw.account).trim() : '',
  }
}

/** B 端当前登录用户上下文（与 AuthSimpleLoginUserResult 对齐，并兼容扩展字段） */
export interface BLoginUserContext {
  id: string
  name: string
  account: string
  /** 组织 ID（部门），接口可能用不同字段名 */
  orgId: string
  /** 组织名称，便于无详情接口时展示 */
  orgName: string
  /**
   * 行政区划相关（若后端扩展返回，或与登录缓存一致）
   * 用于前端回填「所属地区」
   */
  regionCode: string
  roleIdList: string[]
}

function pickFirstStr(raw: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = raw[k]
    if (v != null && String(v).trim() !== '') return String(v).trim()
  }
  return ''
}

/**
 * 从 localStorage `auth_simpleLoginUserJson` 解析回填字段（与接口 data 结构一致，字段名兼容 camel/snake）
 * 无有效 id 时返回 null
 */
export function getSimpleLoginUserBackfillFromStorage(): BLoginUserContext | null {
  const raw = getStoredSimpleLoginUserJson<Record<string, unknown>>()
  if (!raw || typeof raw !== 'object') return null
  const id = raw.id != null ? String(raw.id).trim() : ''
  if (!id) return null
  return {
    id,
    name: raw.name != null ? String(raw.name) : '',
    account: raw.account != null ? String(raw.account) : '',
    orgId: pickFirstStr(raw, ['orgId', 'organizationId', 'org_id', 'departmentId', 'deptId']),
    orgName: pickFirstStr(raw, ['orgName', 'org_name', 'department', 'deptName']),
    regionCode: pickFirstStr(raw, [
      'regionCode',
      'region_code',
      'administrativeCode',
      'administrative_code',
      'addressCode',
      'address_code',
    ]),
    roleIdList: asStringList(raw.roleIdList),
  }
}

/** GET /auth/b/getSimpleLoginUser：用于权限申请等需 userId / 默认组织与角色的场景 */
export async function fetchBLoginUserContext(): Promise<BLoginUserContext> {
  const raw = (await fetchSimpleLoginUserRaw()) as Record<string, unknown>
  cacheGetSimpleLoginUserPayload(raw)
  return {
    id: raw.id != null ? String(raw.id) : '',
    name: raw.name != null ? String(raw.name) : '',
    account: raw.account != null ? String(raw.account) : '',
    orgId: pickFirstStr(raw, ['orgId', 'organizationId', 'org_id', 'departmentId', 'deptId']),
    orgName: pickFirstStr(raw, ['orgName', 'org_name', 'department', 'deptName']),
    regionCode: pickFirstStr(raw, [
      'regionCode',
      'region_code',
      'administrativeCode',
      'administrative_code',
      'addressCode',
      'address_code',
    ]),
    roleIdList: asStringList(raw.roleIdList),
  }
}

/**
 * 拉取当前用户按钮权限码（用于 yj-admin-front 显隐）
 * 与资料接口一致使用 GET /auth/b/getSimpleLoginUser，确保 buttonCodeList 同源。
 */
export async function fetchLoginUserWithPermissions(): Promise<LoginUserWithPermissions> {
  const raw = await fetchSimpleLoginUserRaw()
  cacheGetSimpleLoginUserPayload(raw)
  return loginUserPermissionsFromRaw(raw)
}
